<template>
  <DashboardCard style="padding: 0">
    <template #title>
      <WdsStack gap-sm class="tw-text-gray-900 tw-pt-6 tw-px-6" align-center>
        <span>{{ $t('Needs your approval') }}</span>
        <WdsBadge
          v-if="approvalApprovablesPaginatorInfo.total > 0"
          :count="approvalApprovablesPaginatorInfo.total"
          class="tw-ml-1"
        />
        <WdsButtonGhost v-if="approvalApprovablesPaginatorInfo.total > 0" href="/approvals">
          <WdsStack gap-sm justify-center align-center>
            {{ $t('See all') }}
            <WdsIcon name="arrow-right" :size="16" />
          </WdsStack>
        </WdsButtonGhost>
      </WdsStack>
    </template>
    <div class="tw-px-3 tw-pb-3">
      <template v-if="loading">
        <JobsCardItemSkeleton />
        <JobsCardItemSkeleton />
        <JobsCardItemSkeleton />
      </template>
      <template v-else>
        <BaseShowMore collapsable>
          <template v-if="approvalApprovables.length === 0">
            <div class="tw-px-3 tw-pb-3">
              <slot name="no-results" />
            </div>
          </template>
          <component
            :is="mapTriggerType(approvalApprovable.approval.trigger)"
            v-for="approvalApprovable in approvalApprovables"
            :key="approvalApprovable.id"
            :approvable="approvalApprovable"
          />
          <template #button="{ buttonText }">
            <BaseShowMoreButton class="tw-ml-2" :button-text="buttonText" />
          </template>
        </BaseShowMore>
      </template>
    </div>
  </DashboardCard>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import gql from 'graphql-tag'
import { useQuery } from '@vue/apollo-composable'
import { WdsBadge, WdsButtonGhost, WdsIcon, WdsStack } from '@worksome/wds-components'
import DashboardCard from '@/components/DashboardCard.vue'
import JobsCardItemSkeleton from '@/components/JobsCardItemSkeleton.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import BaseShowMore from '@/components/BaseShowMore.vue'
import BaseShowMoreButton from '@/components/BaseShowMoreButton.vue'
import ApprovalDashboardCardItem from '@/components/Workflows/Approvals/Request/ApprovalDashboardCardItem.vue'
import { ApprovalTrigger } from '@/gql/graphql'

const currentPage = ref(1)
const first = ref(5)

const { result, loading } = useQuery(
  gql`
    query Approvables($page: Int, $first: Int, $accounts: [ID!], $requiresActionUsers: [ID!]) {
      approvalApprovables(
        page: $page
        first: $first
        accounts: $accounts
        requiresActionUsers: $requiresActionUsers
      ) {
        data {
          id
          approval {
            id
            trigger
          }
          approvalStates {
            actionedBy {
              name
            }
            createdAt
          }
          approvable {
            ... on Hire {
              id
              latestContract {
                jobName
              }
              job {
                id
                name
              }
            }
          }
        }
        paginatorInfo {
          total
          currentPage
          hasMorePages
        }
      }
    }
  `,
  () => ({
    accounts: [currentAccount.globalId],
    requiresActionUsers: [currentUser.globalId],
    page: currentPage.value,
    first: first.value,
  }),
  {
    clientId: 'publicApi',
    debounce: 100,
  },
)

let previousResult = {}
const approvalApprovables = computed(() => {
  const response = loading.value ? previousResult : result.value
  previousResult = response
  const { data } = response?.approvalApprovables ?? {}

  return data ?? []
})

let previousPaginatorInfo = {}
const approvalApprovablesPaginatorInfo = computed(() => {
  const response = loading.value ? previousPaginatorInfo : result.value
  previousPaginatorInfo = response

  const { paginatorInfo } = response?.approvalApprovables ?? {}

  return paginatorInfo ?? {}
})

const mapTriggerType = (type) => {
  if (
    type === ApprovalTrigger.HireCreated
    || type === ApprovalTrigger.HireChanged
    || type === ApprovalTrigger.ClassificationCreated
  ) {
    return ApprovalDashboardCardItem
  }

  throw Error('Trigger type not defined')
}
</script>

<style>
.wds-pagination {
  @apply tw-flex tw-items-center;
}
</style>
